import { Loader2 } from "lucide-react";
import React, { useState } from "react";
import { updateFullName } from "../../services/profileService";
import SettingsField from "./SettingsField";

interface ProfileNameFormProps {
	/** Current display name, or "" if the account never had one. */
	initialName: string;
	/** Called after a successful save so the sidebar picks the new name up. */
	onSaved: (name: string) => Promise<void> | void;
}

const INPUT_CLASSES =
	"w-full px-3 py-2 border border-gray-300 rounded-lg text-base text-text-base " +
	"focus:outline-none focus:ring-2 focus:ring-[#008782] focus:border-transparent " +
	"disabled:bg-gray-50 disabled:text-text-muted";

const ProfileNameForm: React.FC<ProfileNameFormProps> = ({
	initialName,
	onSaved,
}) => {
	const [name, setName] = useState(initialName);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [saved, setSaved] = useState(false);

	const trimmed = name.trim();
	const isDirty = trimmed !== initialName.trim();

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		setError(null);
		setSaved(false);

		if (!trimmed) {
			setError("Name cannot be empty.");
			return;
		}

		setSaving(true);
		try {
			await updateFullName(trimmed);
			await onSaved(trimmed);
			setSaved(true);
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Could not save your name.",
			);
		} finally {
			setSaving(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-4 max-w-md">
			<SettingsField label="Display name" htmlFor="settings-full-name">
				<input
					id="settings-full-name"
					type="text"
					value={name}
					onChange={(event) => {
						setName(event.target.value);
						setSaved(false);
					}}
					disabled={saving}
					autoComplete="name"
					className={INPUT_CLASSES}
				/>
			</SettingsField>

			{error && <p className="text-sm text-red-600">{error}</p>}
			{saved && (
				<p className="text-sm text-[#008782]">Name updated.</p>
			)}

			<button
				type="submit"
				disabled={saving || !isDirty}
				className="px-4 py-2 bg-[#008782] text-white rounded-lg text-sm font-medium hover:bg-[#004C4B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
			>
				{saving && <Loader2 className="w-4 h-4 animate-spin" />}
				<span>Save name</span>
			</button>
		</form>
	);
};

export default ProfileNameForm;
